import { useEffect, useState } from 'react';
import { useFetcher, Link } from 'react-router';
import { T } from '@transifex/react';
import { t } from '@transifex/native';
import { XIcon } from 'lucide-react';
import { $path } from 'safe-routes';

import type { components } from '~/api/openapi';
import { Button } from './button';
import {
  CommentNotificationItem,
  SubmissionReactionNotificationItem,
  CommentReactionNotificationItem,
  StarRatingNotificationItem,
} from './notification-list-item';
import { generateCommentQueryParam } from './blocks/overlay-comment/use-comment-state';
import { Sheet, SheetContent, SheetClose } from './sheet';

function buildCommentSearchParams(
  comment: Parameters<typeof generateCommentQueryParam>[0],
  commentId: string
) {
  const params = generateCommentQueryParam(comment) ?? new URLSearchParams();
  params.set('commentId', commentId);
  return params;
}

type NotificationItem =
  | components['schemas']['CommentsReceivedDto']
  | components['schemas']['SubmissionEmojiReactionsReceivedDto']
  | components['schemas']['CommentEmojiReactionsReceivedDto']
  | components['schemas']['StarAwardReceivedDto'];

type NotificationSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  onMarkAllAsRead?: () => void;
};

function getUnseenIds(items: NotificationItem[]) {
  const commentIds: string[] = [];
  const emojiReactionGroupIds: string[] = [];
  const notificationIds: string[] = [];

  for (const item of items) {
    switch (item.type) {
      case 'comment': {
        if (!item.isSeen) {
          commentIds.push(item.id);
        }
        break;
      }
      case 'submissionEmoji': {
        if (!item.submissionEmojiReactions.receiverSeen) {
          emojiReactionGroupIds.push(item.submissionEmojiReactions.emojiReactionGroupId);
        }
        break;
      }
      case 'commentEmoji': {
        if (!item.commentEmojiReactions.receiverSeen) {
          emojiReactionGroupIds.push(item.commentEmojiReactions.emojiReactionGroupId);
        }
        break;
      }
      case 'starAward': {
        if (!item.isSeen) {
          notificationIds.push(item.notificationId);
        }
        break;
      }
    }
  }

  return { commentIds, emojiReactionGroupIds, notificationIds };
}

function markAllAsSeen(items: NotificationItem[]): NotificationItem[] {
  return items.map(item => {
    switch (item.type) {
      case 'comment':
      case 'starAward':
        return { ...item, isSeen: true };
      case 'submissionEmoji':
        return {
          ...item,
          submissionEmojiReactions: { ...item.submissionEmojiReactions, receiverSeen: true },
        };
      case 'commentEmoji':
        return {
          ...item,
          commentEmojiReactions: { ...item.commentEmojiReactions, receiverSeen: true },
        };
      default:
        return item;
    }
  });
}

export function NotificationSheet({ isOpen, onClose, onMarkAllAsRead }: NotificationSheetProps) {
  const fetcher = useFetcher<{ items: NotificationItem[] }>();
  const { data: fetcherData, state: fetcherState, load: fetcherLoad } = fetcher;
  const markSeenFetcher = useFetcher();
  const markItemSeenFetcher = useFetcher();
  const [hasMarkedAllAsRead, setHasMarkedAllAsRead] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setHasMarkedAllAsRead(false);
    fetcherLoad($path('/notifications/resource'));
  }, [isOpen, fetcherLoad]);

  const isLoading = fetcherState === 'loading' && !fetcherData;
  const rawItems = fetcherData?.items || [];
  const isOptimisticallyRead = hasMarkedAllAsRead || markSeenFetcher.state !== 'idle';
  const items = isOptimisticallyRead ? markAllAsSeen(rawItems) : rawItems;
  const hasItems = items.length > 0;
  const { commentIds, emojiReactionGroupIds, notificationIds } = getUnseenIds(rawItems);
  const hasUnseen =
    !isOptimisticallyRead &&
    (commentIds.length > 0 || emojiReactionGroupIds.length > 0 || notificationIds.length > 0);

  const handleMarkAllAsRead = () => {
    if (Math.max(commentIds.length, emojiReactionGroupIds.length, notificationIds.length) <= 0)
      return;

    setHasMarkedAllAsRead(true);
    onMarkAllAsRead?.();

    const formData = new FormData();
    for (const id of commentIds) {
      formData.append('commentIds', id);
    }
    for (const id of emojiReactionGroupIds) {
      formData.append('emojiReactionGroupIds', id);
    }
    for (const id of notificationIds) {
      formData.append('notificationIds', id);
    }
    markSeenFetcher.submit(formData, {
      method: 'PUT',
      action: $path('/comments/seen'),
    });
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        side="right"
        hideClose
        overlayClassName="bg-transparent"
        className="
          w-full gap-0 overflow-hidden bg-background-0
          md:max-w-[800px]
        "
        aria-label={t('Notifications')}
      >
        {/* Header */}
        <div className="flex items-center gap-sm px-lg pt-lg pb-md">
          <h2 className="text-h2 text-text-900">
            <T _str="Notifications" />
          </h2>
          <div className="ml-auto flex items-center gap-sm">
            {hasUnseen && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllAsRead}
                disabled={markSeenFetcher.state !== 'idle'}
              >
                <T _str="Mark all as read" />
              </Button>
            )}
            <SheetClose asChild>
              <button
                className="
                  cursor-pointer rounded-xs opacity-70 transition-opacity
                  hover:opacity-100
                "
                aria-label={t('Close notifications')}
              >
                <XIcon className="size-4" />
              </button>
            </SheetClose>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="py-xl text-center text-text-500">
              <T _str="Loading notifications..." />
            </div>
          ) : !hasItems ? (
            <div className="py-xl text-center text-text-500">
              <T _str="No notifications yet" />
            </div>
          ) : (
            <ul>
              {items.map((item, index) => {
                switch (item.type) {
                  case 'comment':
                    return (
                      <li key={item.id} className="border-b border-line-300">
                        <Link
                          to={$path(
                            '/submissions/:submissionId',
                            { submissionId: item.submissionId },
                            buildCommentSearchParams(item, item.id)
                          )}
                          onClick={onClose}
                        >
                          <CommentNotificationItem comment={item} />
                        </Link>
                      </li>
                    );
                  case 'submissionEmoji':
                    return (
                      <li
                        key={`reaction-${item.submissionContext.submissionId}-${index}`}
                        className="border-b border-line-300"
                      >
                        <Link
                          to={$path('/submissions/:submissionId', {
                            submissionId: item.submissionContext.submissionId,
                          })}
                          onClick={onClose}
                        >
                          <SubmissionReactionNotificationItem reaction={item} />
                        </Link>
                      </li>
                    );
                  case 'commentEmoji':
                    return (
                      <li
                        key={`reaction-${item.submissionContext.submissionId}-${index}`}
                        className="border-b border-line-300"
                      >
                        <Link
                          to={$path(
                            '/submissions/:submissionId',
                            {
                              submissionId: item.submissionContext.submissionId,
                            },
                            buildCommentSearchParams(item.comment, item.commentId)
                          )}
                          onClick={onClose}
                        >
                          <CommentReactionNotificationItem reaction={item} />
                        </Link>
                      </li>
                    );
                  case 'starAward':
                    return (
                      <li key={`star-${item.notificationId}`} className="border-b border-line-300">
                        <Link
                          to={$path('/submissions/:submissionId', {
                            submissionId: item.submissionContext.submissionId,
                          })}
                          onClick={() => {
                            if (!item.isSeen) {
                              const formData = new FormData();
                              formData.append('notificationIds', item.notificationId);
                              markItemSeenFetcher.submit(formData, {
                                method: 'PUT',
                                action: $path('/comments/seen'),
                              });
                            }
                            onClose();
                          }}
                        >
                          <StarRatingNotificationItem
                            starsGiven={item.rating.stars ?? 0}
                            sectionTitle={item.submissionContext.sectionTitle}
                            sectionType={item.submissionContext.sectionType}
                            journeyName={item.submissionContext.journeyName}
                            ratedAt={item.notificationTime}
                            isSeen={item.isSeen}
                          />
                        </Link>
                      </li>
                    );
                  default:
                    return null;
                }
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
